import React, { useEffect } from "react";
import { createPortal } from "react-dom";

import { deploymentConfig } from "./deploymentConfig";
import type { DeploymentOverlayCopy } from "./types";

export interface DeploymentOverlayProps {
  copy: DeploymentOverlayCopy;
  countdownSeconds: number;
  onUpdateNow: () => void;
}

const OVERLAY_Z_INDEX = 2147483646;

const COUNTDOWN_ANIMATION_STYLE = `
  @keyframes deployment-countdown-enter {
    from {
      opacity: 0;
      transform: scale(0.88);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .deployment-countdown-digit {
    display: inline-block;
    animation: deployment-countdown-enter 0.35s ease-out;
  }
`;

/**
 * Full-screen blocking overlay shown when a deployment is detected.
 * Prevents interaction, scrolling, and keyboard input until reload.
 */
export function DeploymentOverlay({
  copy,
  countdownSeconds,
  onUpdateNow,
}: DeploymentOverlayProps) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const blockInteraction = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const blockKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        event.preventDefault();
      }
    };

    document.addEventListener("wheel", blockInteraction, { capture: true });
    document.addEventListener("touchmove", blockInteraction, { capture: true });
    document.addEventListener("keydown", blockKeyboard, { capture: true });

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("wheel", blockInteraction, { capture: true });
      document.removeEventListener("touchmove", blockInteraction, {
        capture: true,
      });
      document.removeEventListener("keydown", blockKeyboard, { capture: true });
    };
  }, []);

  const content = (
    <>
      <style>{COUNTDOWN_ANIMATION_STYLE}</style>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="deployment-overlay-title"
        aria-describedby="deployment-overlay-description"
        aria-live="polite"
        style={styles.backdrop}
      >
        <div style={styles.panel}>
          <h1 id="deployment-overlay-title" style={styles.title}>
            {copy.title}
          </h1>
          <p id="deployment-overlay-description" style={styles.message}>
            {copy.message}
          </p>
          <p style={styles.countdownLine}>
            {copy.countdownLabel}{" "}
            <span
              key={countdownSeconds}
              className="deployment-countdown-digit"
              style={styles.countdownValue}
            >
              {countdownSeconds}
            </span>
          </p>
          <button
            type="button"
            onClick={onUpdateNow}
            style={styles.button}
            autoFocus
          >
            {copy.updateNowLabel}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: OVERLAY_Z_INDEX,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(11, 11, 11, 0.55)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    padding: "24px",
  },
  panel: {
    width: "100%",
    maxWidth: "420px",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 16px 48px rgba(11, 11, 11, 0.18)",
    padding: "32px 28px",
    textAlign: "center",
  },
  title: {
    margin: 0,
    fontSize: "20px",
    fontWeight: 700,
    color: "#0B0B0B",
    lineHeight: 1.3,
  },
  message: {
    margin: "12px 0 0",
    fontSize: "15px",
    fontWeight: 400,
    color: "#606060",
    lineHeight: 1.5,
  },
  countdownLine: {
    margin: "24px 0 0",
    fontSize: "15px",
    color: "#0B0B0B",
    lineHeight: 1.5,
  },
  countdownValue: {
    minWidth: "1.25em",
    fontSize: "28px",
    fontWeight: 700,
    color: "#1a4677",
    verticalAlign: "middle",
  },
  button: {
    marginTop: "24px",
    width: "100%",
    padding: "12px 20px",
    fontSize: "15px",
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: "#1a4677",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
};

/** Default overlay copy sourced from configuration. */
export function getDefaultOverlayCopy(): DeploymentOverlayCopy {
  return deploymentConfig.overlay;
}
